import React from 'react';
import { isTeamMember, Person, TeamPermission, TeamRole, useProjectRolesService } from 'nekst-api';
import { useAuthorizationChecker } from 'authorization-scope';
import { useDataLoaderNew } from 'data-loader';
import { ProjectRolesMultiDropdown, Separator } from 'features/nekst-widgets';
import { CheckboxInput, useFormContextRequired } from 'ui-builder';

function useOwnerManagerRoleId(person: Person): number | null {
  const projectRolesService = useProjectRolesService();
  const isOwner = person.teamRole === TeamRole.OWNER;

  const { data: managerRole } = useDataLoaderNew(
    async () => {
      if (!isOwner) {
        return null;
      }
      return projectRolesService.getManagerRole();
    },
    { dependencies: [isOwner] },
  );

  return managerRole?.id ?? null;
}

export function AutoAssignRolesInput(props: { person: Person }) {
  const { isGranted } = useAuthorizationChecker();
  const isOwner = props.person.teamRole === TeamRole.OWNER;
  const managerRoleId = useOwnerManagerRoleId(props.person);
  const formContext = useFormContextRequired();

  if (!isTeamMember(props.person.teamRole) || !isGranted(TeamPermission.USE_TEAM_MEMBERS_FEATURE)) {
    return null;
  }

  return (
    <>
      <Separator />
      <ProjectRolesMultiDropdown
        source="autoAssignRolesIds"
        label="Auto-Assign Role(s)"
        lockedRolesIds={managerRoleId !== null ? [managerRoleId] : undefined}
        onChangeCallback={!isOwner ? (values: Record<string, any>) => {
          const newRoles = values.autoAssignRolesIds || [];
          if (newRoles.length === 0) {
            formContext.onChangeCallback({ ...values, autoAddToTransaction: false });
          } else {
            formContext.onChangeCallback(values);
          }
        } : undefined}
      />
      <CheckboxInput
        source="autoAddToTransaction"
        label="Add to every Transaction when created"
        value={isOwner ? true : undefined}
        isDisabled={isOwner || ((data: any) => {
          const roles: number[] = data.autoAssignRolesIds || [];
          return roles.length === 0;
        })}
      />
    </>
  );
}
